import api from '../api/axios';

const withBase = (path: string) => `/video-dubbing-outputs${path}`;

export interface TranslatedSegment {
  speaker: number;
  startTime: number;
  endTime: number;
  originalText: string;
  translatedText: string;
  isEdited?: boolean;
  editedBy?: string;
  editedAt?: Date;
}

export interface TranslatedTranscript {
  segments: TranslatedSegment[];
  totalDuration: number;
  targetLanguage: string;
}

export interface AudioSegment {
  speaker: number;
  characterName?: string;
  audioUrl: string;
  startTime: number;
  endTime: number;
  voiceId: string;
}

export interface VideoDubbingOutput {
  id: string;
  dubbingJobId: string;
  transcriptionId: string;
  title: string | null;
  description: string | null;
  targetLanguage: string;
  translatedTranscript: TranslatedTranscript | null;
  audioSegments: AudioSegment[] | null;
  finalVideoUrl: string | null;
  status: 'draft' | 'translating' | 'translated' | 'generating_audio' | 'audio_generated' | 'rendering_video' | 'completed' | 'failed';
  command: string | null;
  isApproved: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVideoDubbingOutputDto {
  dubbingJobId: string;
  transcriptionId: string;
  title?: string | null;
  description?: string | null;
  targetLanguage: string;
  command?: string | null;
}

export const createVideoDubbingOutput = async (payload: CreateVideoDubbingOutputDto) => {
  const res = await api.post(withBase(''), payload);
  return res.data as VideoDubbingOutput;
};

export const getVideoDubbingOutputs = async () => {
  const res = await api.get(withBase(''));
  return res.data as VideoDubbingOutput[];
};

export const getVideoDubbingOutputsByDubbingJobId = async (dubbingJobId: string) => {
  const res = await api.get(withBase(`/dubbing-job/${dubbingJobId}`));
  return res.data as VideoDubbingOutput[];
};

export const getVideoDubbingOutputsByTranscriptionId = async (transcriptionId: string) => {
  const res = await api.get(withBase(`/transcription/${transcriptionId}`));
  return res.data as VideoDubbingOutput[];
};

export const getVideoDubbingOutput = async (id: string) => {
  const res = await api.get(withBase(`/${id}`));
  return res.data as VideoDubbingOutput;
};

export const updateVideoDubbingOutput = async (id: string, payload: Partial<CreateVideoDubbingOutputDto>) => {
  const res = await api.patch(withBase(`/${id}`), payload);
  return res.data as VideoDubbingOutput;
};

export const updateTranslatedTranscript = async (id: string, translatedTranscript: TranslatedTranscript) => {
  const res = await api.patch(withBase(`/${id}/translated-transcript`), { translatedTranscript });
  return res.data as VideoDubbingOutput;
};

export const setCommand = async (id: string, command: 'process' | 're-render') => {
  const res = await api.patch(withBase(`/${id}/command`), { command });
  return res.data as VideoDubbingOutput;
};

export const approveOutput = async (id: string, approvedBy: string) => {
  const res = await api.patch(withBase(`/${id}/approve`), { approvedBy });
  return res.data as VideoDubbingOutput;
};

export const deleteVideoDubbingOutput = async (id: string) => {
  const res = await api.delete(withBase(`/${id}`));
  return res.data;
};

const videoDubbingOutputsService = {
  createVideoDubbingOutput,
  getVideoDubbingOutputs,
  getVideoDubbingOutputsByDubbingJobId,
  getVideoDubbingOutputsByTranscriptionId,
  getVideoDubbingOutput,
  updateVideoDubbingOutput,
  updateTranslatedTranscript,
  setCommand,
  approveOutput,
  deleteVideoDubbingOutput,
};

export default videoDubbingOutputsService;
